import React, { useCallback, useMemo, useRef } from 'react';

import { useEditorDoc, useEditorDispatch, useEditorUi } from '../../EditorProvider';
import type { CrosswordDoc, WordDraft } from './doc';
import type { CrosswordAction } from './reducer';
import { projectGrid } from './project';
import { Cell } from './Cell';
import { initialCrosswordUi, type CrosswordUi } from './ui';

// O ÚNICO consumidor do DocContext. A grade é o MAPA: clicar numa casa vazia coloca uma
// casa de dica (e a seleciona); clicar numa dica ou numa letra seleciona a dica para
// editar no painel. O conteúdo (palavra + dica) é digitado no Inspector, não aqui.
//
// Cada `Cell` é memo com props primitivas, então re-desenhar a grade só re-renderiza a
// célula que mudou.

/** Casas ocupadas por uma palavra. */
function wordCells(w: WordDraft): { x: number; y: number }[] {
  const cells: { x: number; y: number }[] = [];
  for (let i = 0; i < w.texto.length; i++) {
    cells.push(w.direcao ? { x: w.x, y: w.y + i } : { x: w.x + i, y: w.y });
  }
  return cells;
}

export const Canvas: React.FC = () => {
  const doc = useEditorDoc<CrosswordDoc>();
  const dispatchApi = useEditorDispatch();
  const [ui, setUi] = useEditorUi<CrosswordUi>();

  const stateRef = useRef({ doc, ui });
  stateRef.current = { doc, ui };

  const dispatch = useCallback(
    (action: CrosswordAction) => dispatchApi.do(action),
    [dispatchApi],
  );

  const onCell = useCallback(
    (x: number, y: number) => {
      const { doc: d } = stateRef.current;
      const dead = d.deadCells.find((c) => c.x === x && c.y === y);
      if (dead) {
        setUi((prev) => ({ ...prev, selectedDead: { x, y } }));
        return;
      }
      const owner = d.words.find((w) => wordCells(w).some((c) => c.x === x && c.y === y));
      if (owner) {
        // clicar numa letra seleciona a casa de dica que a pendura
        const clue = d.deadCells.find(
          (c) => c.palavra1Ref === owner.ref || c.palavra2Ref === owner.ref,
        );
        if (clue) setUi((prev) => ({ ...prev, selectedDead: { x: clue.x, y: clue.y } }));
        return;
      }
      // casa vazia → coloca dica e já seleciona
      dispatch({ type: 'ADD_DEAD_CELL', x, y });
      setUi((prev) => ({ ...prev, selectedDead: { x, y } }));
    },
    [dispatch, setUi],
  );

  const grid = useMemo(() => projectGrid(doc), [doc]);

  // Destaque: as casas das respostas da dica selecionada.
  const highlighted = useMemo(() => {
    const set = new Set<string>();
    if (!ui.selectedDead) return set;
    const cell = doc.deadCells.find(
      (c) => c.x === ui.selectedDead!.x && c.y === ui.selectedDead!.y,
    );
    if (!cell) return set;
    for (const ref of [cell.palavra1Ref, cell.palavra2Ref]) {
      const w = ref != null ? doc.words.find((word) => word.ref === ref) : undefined;
      if (w) for (const c of wordCells(w)) set.add(`${c.x},${c.y}`);
    }
    return set;
  }, [doc.deadCells, doc.words, ui.selectedDead]);

  const size = Math.max(26, Math.min(46, Math.floor(620 / doc.largura)));

  return (
    <div role="grid" aria-label="Grade da cruzada" style={{ display: 'inline-block', lineHeight: 0 }}>
      {grid.map((row, y) => (
        <div key={y} role="row" style={{ display: 'flex' }}>
          {row.map((cell) => (
            <Cell
              key={cell.x}
              x={cell.x}
              y={cell.y}
              kind={cell.kind}
              letter={cell.letter}
              number={cell.numbers.length > 0 ? cell.numbers[0] : null}
              clue={cell.clue}
              conflict={cell.conflict}
              isFocused={highlighted.has(`${cell.x},${cell.y}`)}
              isSelected={
                ui.selectedDead != null && ui.selectedDead.x === cell.x && ui.selectedDead.y === cell.y
              }
              size={size}
              onClick={onCell}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export { initialCrosswordUi };
